'use client';
import { GetInfoItemsResult, InfoBarModule, InfoBarPriority } from './InfoBarModule';
import React from 'react';
import styled from '@benzinga/themetron';

import { MarketingWin, MarketingWinsManager } from '@benzinga/marketing-wins-manager';
import {
  HorizontalRuleModule,
  ProTickerPopover,
  QuoteData,
  SparkLineModule,
  TickerHeaderModule,
  TickerNotesModule,
} from '../../TickerHover';
import { PerformanceColor } from '../../Ticker';
import { SessionContext } from '@benzinga/session-context';
import { StockSymbol } from '@benzinga/session';

export class MarketingWinsModule extends InfoBarModule {
  public id = 'marketing-wins';
  public name = 'Pro Wins';
  public priority = InfoBarPriority.Base;
  public getInfoItems: Generator<GetInfoItemsResult, GetInfoItemsResult, number>;
  private wins: MarketingWin[] = [];
  private marketingWinsManager: MarketingWinsManager;
  private onClick?: (id: string) => void;
  private onSymbolClick: (symbol: StockSymbol) => void;
  private count: number;

  constructor(
    marketingWinsManager: MarketingWinsManager,
    count: number,
    onSymbolClick: (symbol: StockSymbol) => void,
    onClick?: (id: string) => void,
  ) {
    super();
    this.marketingWinsManager = marketingWinsManager;
    this.onClick = onClick;
    this.count = count;
    this.getInfoItems = this.marketingWinsItemsGenerator();
    this.onSymbolClick = onSymbolClick;

    marketingWinsManager.getWins(false, { limit: count }).then(w => {
      this.wins = w.ok ?? [];
    });
  }
  createNode(win: MarketingWin) {
    const { id, percentGain, symbol, theWinDetailed } = win;
    const { onClick } = this;
    return (
      <MarketingWinsWrapper onClick={onClick ? () => onClick(id) : undefined}>
        <TheWinHeader>PRO WIN:</TheWinHeader>
        <TheWin>'{theWinDetailed}'</TheWin>
        <TheWinInfo>
          {'-'}
          <ProTickerPopover
            modules={
              <>
                <TickerHeaderModule onSymbolClick={this.onSymbolClick} />
                <HorizontalRuleModule />
                <SparkLineModule />
                <HorizontalRuleModule />
                <QuoteData symbol={symbol} />
                <HorizontalRuleModule />
                <TickerNotesModule />
              </>
            }
            positions={['top']}
            symbol={symbol}
          >
            <TheWinInfoTicker>
              <Spacer />
              {symbol}
              <Spacer />
              <PerformanceColor isPercentage={true} stat={percentGain}></PerformanceColor>
            </TheWinInfoTicker>
          </ProTickerPopover>
        </TheWinInfo>
      </MarketingWinsWrapper>
    );
  }

  *marketingWinsItemsGenerator(): Generator<GetInfoItemsResult, GetInfoItemsResult, number> {
    let index = 0;

    while (true) {
      const { count, marketingWinsManager, wins } = this;
      // eslint-disable-next-line no-loop-func
      marketingWinsManager.getWins(false, { limit: count }).then(w => {
        this.wins = w.ok ?? [];
      });

      if (wins.length === 0) {
        index = 0;
        yield { items: [] };
        continue;
      }

      index = (index + 1) % wins.length;
      yield {
        items: [{ node: this.createNode(wins[index]), width: 0 }],
      };
    }
  }
}

export const useMarketingWinsModule = (
  count: number,
  onSymbolClick: (symbol: StockSymbol) => void,
  onClick?: (id: string) => void,
): MarketingWinsModule => {
  const session = React.useContext(SessionContext);
  const marketingWinsManager = session.getManager(MarketingWinsManager);
  const module = React.useMemo<MarketingWinsModule>(
    () => new MarketingWinsModule(marketingWinsManager, count, onSymbolClick, onClick),
    [count, onClick, onSymbolClick, marketingWinsManager],
  );

  return module;
};

const MarketingWinsWrapper = styled.span`
  display: flex;
  flex-grow: 1;
  flex-basis: 0;
  justify-content: start;
  max-width: 100%;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  padding-left: 20px;
`;

const TheWin = styled.span`
  display: block;
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
  margin: 0 5px 0 5px;
`;

const TheWinHeader = styled.span`
  font-weight: bolder;
  margin: 0 5px 0 10px;
  text-wrap: nowrap;
`;

const TheWinInfo = styled.span`
  align-items: center;
  display: flex;
`;

const TheWinInfoTicker = styled.span`
  align-items: center;
  display: flex;
`;

const Spacer = styled.span`
  margin-left: 5px;
`;
